<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  useTemplateRef,
} from "vue";
import { useRouter, useRoute, onBeforeRouteLeave } from "vue-router";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useQuotesStore } from "@/stores/modules/quotes/quotes";
import { useQuoteFilterStore } from "@/stores/modules/quotes/quotes-filter";
import { useQuoteManagement } from "@/composables/useQuoteManagement";
import { useAppReviewPrompt } from "@/composables/useAppReviewPrompt";
import { QuoteLayout } from "@/enums/quote-layout";
import { DynamicScroller, DynamicScrollerItem } from "vue-virtual-scroller";
import Quote from "../components/Quote.vue";
import QuoteGridItem from "@/components/quotes/quote-grid-item.vue";
import QuoteDetailBottomSheet from "@/components/quotes/quote-detail-bottom-sheet.vue";
import InformationText from "../components/InformationText.vue";
import TextInput from "@/components/Inputs/TextInput.vue";
import { ViewGridIcon, ViewListIcon } from "@heroicons/vue/outline";
import AddQuoteBottomSheet from "@/components/AddQuoteBottomSheet.vue";
import EditQuoteBottomSheet from "@/components/EditQuoteBottomSheet.vue";
import type { QuoteDocument } from "@kekkle/shared/types/documents/quote-document/quote-document";
import SkeletonLoader from "../components/ui/SkeletonLoader.vue";
import KekkleProgressBar from "../components/ui/KekkleProgressBar.vue";

const router = useRouter();
const route = useRoute();
const groupsStore = useGroupsStore();
const quotesStore = useQuotesStore();
const quoteFilterStore = useQuoteFilterStore();
const { addQuoteBottomSheetOpen, editQuoteBottomSheetOpen, selectedQuoteData } =
  useQuoteManagement();
const { maybePromptReview } = useAppReviewPrompt();

const searchTerm = ref("");
const showSearchInput = ref(false);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const scrollerRef = useTemplateRef<any>("scroller");
const pendingScrollRestore = ref(
  quotesStore.scrollIndex > 0 || quotesStore.scrollOffset > 0,
);

/* Grid view detail */
const quoteDetailOpen = ref(false);
const detailQuoteData = ref<QuoteDocument | null>(null);

/* Scroll time tracking for app review prompt */
let scrollTimeAccumulated = 0;
let scrollTimerId: ReturnType<typeof setInterval> | null = null;
let isCurrentlyScrolling = false;
let scrollIdleTimeout: ReturnType<typeof setTimeout> | null = null;

const quotes = computed(() => quotesStore.quotes);
const loadingInitialQuotes = computed(() => quotesStore.loadingInitialQuotes);
const isStale = computed(() => quotesStore.isStale);
const fetchError = computed(() => quotesStore.fetchError);
const groupData = computed(() => groupsStore.groupData);
const isListMode = computed(
  () => quoteFilterStore.layoutMode === QuoteLayout.List,
);

const searchEnabled = computed(
  () => showSearchInput.value || !!quoteFilterStore.searchText,
);

const allQuotesEnabled = computed(() => !quoteFilterStore.hasActiveFilters());
const authorFilterUuid = computed(() => quoteFilterStore.authorUuid);
const dateFilter = computed(() => quoteFilterStore.dateFilter);

const validSearch = computed(() => {
  return true;
});

const quoteFilterAuthorName = computed(
  () =>
    groupsStore.activeGroupMembersSorted.find(
      (member) => member.uuid === authorFilterUuid.value,
    )?.name,
);

function formatDateForTitle(date: Date): string {
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function toggleLayout(): void {
  quoteFilterStore.setLayoutMode(
    isListMode.value ? QuoteLayout.Grid : QuoteLayout.List,
  );
}

function onScrollerUpdate(): void {
  // Track scroll time for app review prompt
  isCurrentlyScrolling = true;
  if (scrollIdleTimeout) clearTimeout(scrollIdleTimeout);
  scrollIdleTimeout = setTimeout(() => {
    isCurrentlyScrolling = false;
  }, 1500);
}

function saveScrollState(): void {
  const scroller = scrollerRef.value;
  if (!scroller) return;
  const scrollTop = (scroller.$el as HTMLElement).scrollTop;
  const index = scroller.findItemIndex(scrollTop);
  const itemOffset = scroller.getItemOffset(index);
  quotesStore.setScrollState(
    index,
    scrollTop - itemOffset,
    scroller.cacheSnapshot,
  );
}

function goToComments(e: { quoteId: string; groupId: string }): void {
  router.push({
    name: "commentQuote",
    params: { quoteId: e.quoteId, groupId: e.groupId },
  });
}

function openQuoteDetail(quoteData: QuoteDocument): void {
  detailQuoteData.value = quoteData;
  quoteDetailOpen.value = true;
}

function searchQuotes(): void {
  quoteFilterStore.setSearchText(searchTerm.value);
}

function enableSearch(): void {
  showSearchInput.value = true;
  searchTerm.value = "";
  quoteFilterStore.setSearchText(null);
}

function enableAll(): void {
  quoteFilterStore.clearAllFilters();
  searchTerm.value = "";
  showSearchInput.value = false;

  if (route.query.date) {
    router.replace({ name: "Quotes", query: {} });
  }
}

function clearDateFilter(): void {
  quoteFilterStore.setDateFilter(null);

  if (route.query.date) {
    router.replace({ name: "Quotes", query: {} });
  }
}

// Load date filter from URL query if present (for memory features)
quoteFilterStore.loadFiltersFromUrl();

// Initialize search UI state if there's already a search filter
if (quoteFilterStore.searchText) {
  showSearchInput.value = true;
  searchTerm.value = quoteFilterStore.searchText;
}

// Restore scroll position once a scroller has rendered quotes
watch(
  [quotes, scrollerRef],
  ([newQuotes, scroller]) => {
    if (!pendingScrollRestore.value || newQuotes.length === 0 || !scroller)
      return;

    pendingScrollRestore.value = false;

    scroller.restoreCache(quotesStore.scrollerCache);

    requestAnimationFrame(() => {
      scroller.scrollToItem(quotesStore.scrollIndex, {
        offset: quotesStore.scrollOffset,
      });
    });
  },
  { immediate: true },
);

onMounted(() => {
  // Scroll time tracking for app review prompt
  scrollTimerId = setInterval(() => {
    if (isCurrentlyScrolling) {
      scrollTimeAccumulated += 1;
      if (scrollTimeAccumulated >= 60) {
        maybePromptReview();
        if (scrollTimerId) clearInterval(scrollTimerId);
      }
    }
  }, 1000);

  // Subscribe to quotes for the active group
  quotesStore.subscribeToQuotes();
});

onBeforeRouteLeave(() => {
  saveScrollState();
});

onUnmounted(() => {
  if (scrollTimerId) clearInterval(scrollTimerId);
  if (scrollIdleTimeout) clearTimeout(scrollIdleTimeout);
});
</script>

<template>
  <div class="flex h-screen flex-col pb-16">
    <h1
      class="w-full max-w-xl shrink-0 border-b-2 border-b-kekkle-red bg-white py-4 text-center text-xl font-medium dark:bg-kekkle-dark-dark dark:text-slate-200"
    >
      {{ $t("pages.quotes.title_of") }}
      {{ groupData?.name ?? "" }}
    </h1>
    <KekkleProgressBar :active="isStale" color="red" />
    <div class="my-2 shrink-0 px-2">
      <InformationText
        v-if="fetchError && !loadingInitialQuotes"
        color="red"
        class="mx-auto"
        :information="fetchError"
      />
      <InformationText
        v-if="
          !fetchError &&
          quotes.length === 0 &&
          !loadingInitialQuotes &&
          !searchEnabled &&
          !dateFilter
        "
        class="mx-auto"
        :information="$t('pages.quotes.no_quotes_yet')"
      />
      <InformationText
        v-if="
          !fetchError &&
          quotes.length === 0 &&
          !loadingInitialQuotes &&
          dateFilter
        "
        dismissable
        class="mx-auto"
        :information="
          $t('pages.quotes.no_quotes_on_date', {
            date: formatDateForTitle(dateFilter),
          })
        "
        @dismiss="clearDateFilter"
      />
      <div
        v-if="quotes.length > 0 || searchEnabled"
        class="scrollbar-hide flex w-full snap-x flex-nowrap items-center justify-center gap-x-4 overflow-x-scroll py-2"
      >
        <button
          class="flex h-10 w-10 shrink-0 snap-start items-center justify-center rounded-lg border-2 border-black-dark bg-white transition-all hover:shadow-red-hard-sm active:shadow-none dark:bg-kekkle-dark-light"
          @click="toggleLayout"
        >
          <ViewGridIcon v-if="isListMode" class="h-5 w-5 text-kekkle-red" />
          <ViewListIcon v-else class="h-5 w-5 text-kekkle-red" />
        </button>
        <span
          class="cursor-pointer snap-start rounded-sm border border-kekkle-red p-2 px-4"
          :class="
            allQuotesEnabled
              ? 'bg-kekkle-red text-white'
              : 'bg-white text-kekkle-red dark:bg-kekkle-dark-dark'
          "
          @click="enableAll"
          >{{ $t("pages.quotes.filter_everything") }}</span
        >
        <span
          v-if="!authorFilterUuid && !dateFilter"
          class="cursor-pointer snap-start rounded-sm border border-kekkle-red p-2 px-4"
          :class="
            searchEnabled
              ? 'bg-kekkle-red text-white'
              : 'bg-white text-kekkle-red dark:bg-kekkle-dark-dark'
          "
          @click="enableSearch"
          >{{ $t("pages.quotes.filter_search") }}</span
        >
        <span
          v-if="authorFilterUuid"
          class="cursor-pointer snap-start rounded-sm border border-kekkle-red bg-kekkle-red p-2 px-4 text-white"
          @click="enableAll"
          >{{ quoteFilterAuthorName }}</span
        >
        <span
          v-if="dateFilter"
          class="cursor-pointer snap-start rounded-sm border border-kekkle-red bg-kekkle-red p-2 px-4 text-white"
          @click="enableAll"
          >{{ formatDateForTitle(dateFilter) }}</span
        >
      </div>
      <div v-if="searchEnabled" class="mx-auto mb-2 max-w-md">
        <TextInput
          class="mb-2"
          :label="$t('global.inputs.labels.search_term')"
          :error-message="$t('global.inputs.errors.search_term')"
          :has-error="!validSearch"
          :placeholder="$t('global.inputs.placeholders.search_term')"
          @update:model-value="
            (e) => {
              searchTerm = e;
              searchQuotes();
            }
          "
        />
      </div>
    </div>

    <div v-if="loadingInitialQuotes" class="m-4">
      <SkeletonLoader></SkeletonLoader>
    </div>

    <!-- List view with virtual scroller -->
    <DynamicScroller
      v-if="isListMode"
      ref="scroller"
      :items="quotes"
      :min-item-size="180"
      :buffer="600"
      key-field="_id"
      :emit-update="false"
      class="min-h-0 flex-1 px-2"
      @update="onScrollerUpdate"
    >
      <template #default="{ item, index, active }">
        <DynamicScrollerItem
          :item="item"
          :active="active"
          :data-index="index"
          :size-dependencies="[
            item.quote,
            item.context,
            item.authors,
            item.reported_by,
          ]"
        >
          <div class="pb-2">
            <Quote
              :quote-data="item"
              :quote-id="item._id"
              @go-to-comments="goToComments"
            />
          </div>
        </DynamicScrollerItem>
      </template>
    </DynamicScroller>

    <!-- Grid view -->
    <div v-else class="min-h-0 flex-1 overflow-y-auto px-2">
      <div class="grid grid-cols-2 gap-2">
        <QuoteGridItem
          v-for="quote in quotes"
          :key="quote._id"
          :quote-data="quote"
          :quote-id="quote._id"
          @click="openQuoteDetail"
        />
      </div>
    </div>
  </div>

  <!-- Bottom Sheets -->
  <AddQuoteBottomSheet v-model:open="addQuoteBottomSheetOpen" />
  <EditQuoteBottomSheet
    v-model:open="editQuoteBottomSheetOpen"
    :quote-data="selectedQuoteData"
  />
  <QuoteDetailBottomSheet
    v-model:open="quoteDetailOpen"
    :quote-data="detailQuoteData"
    @go-to-comments="goToComments"
  />
</template>
